import { useEffect, useState } from 'react';
import { X, Share2, Plus, Download, Smartphone, ArrowDown } from 'lucide-react';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const UA = () => navigator.userAgent || '';
const isIos = () => /iphone|ipad|ipod/i.test(UA());
const isAndroid = () => /android/i.test(UA());
// Safari on iOS (exclude Chrome/Firefox/Edge in-app browsers)
const isIosSafari = () => isIos() && /safari/i.test(UA()) && !/crios|fxios|edgios/i.test(UA());

const DISMISS_KEY = 'chinook_install_banner_dismissed_at';

/**
 * InstallBanner - auto-appearing, low-friction install prompt.
 * - Android/desktop (Chrome/Edge): one-tap native install.
 * - iPhone (Safari): auto-shows a clear "Share -> Add to Home Screen" hint
 *   (Apple provides no auto-install API, so this is the least-friction path).
 * - iPhone (non-Safari): tells the user to open in Safari.
 * Dismissible; stays hidden for 7 days after dismissal.
 */
export const InstallBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    const onInstalled = () => setShow(false);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // iOS has no install event — show the hint after a short delay
    let t;
    if (isIos()) t = setTimeout(() => setShow(true), 1800);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (e) { /* ignore */ }
      setDeferred(null);
      setShow(false);
    }
  };

  if (!show) return null;

  const iosSafari = isIosSafari();
  const iosOther = isIos() && !iosSafari;

  return (
    <div className="fixed bottom-4 inset-x-3 sm:inset-x-auto sm:right-4 sm:w-96 z-[60]" data-testid="install-banner">
      <div className="relative rounded-2xl bg-[#002036] border border-[#00C0C0]/40 shadow-2xl p-4 text-white">
        <button onClick={dismiss} className="absolute top-2 right-2 text-slate-400 hover:text-white" data-testid="install-banner-close" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-[#00C0C0]/15 flex-shrink-0">
            <Smartphone className="h-6 w-6 text-[#00C0C0]" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold font-['Outfit']">Install the Chinook app</p>

            {/* Android / desktop: one-tap install */}
            {deferred && (
              <>
                <p className="text-sm text-[#94A3B8] mt-0.5">Add it to your device for one-tap access.</p>
                <button onClick={install}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#00C0C0] text-[#001828] font-semibold text-sm px-4 py-2 hover:bg-[#00D0E0]"
                  data-testid="install-banner-btn">
                  <Download className="h-4 w-4" /> Install now
                </button>
              </>
            )}

            {/* iOS Safari: guided steps */}
            {!deferred && iosSafari && (
              <p className="text-sm text-[#cbd5e1] mt-1 leading-relaxed">
                Tap the <Share2 className="h-4 w-4 inline mx-0.5 text-[#00D0E0]" /> <strong>Share</strong> button below,
                then <Plus className="h-4 w-4 inline mx-0.5 text-[#00D0E0]" /> <strong>Add to Home Screen</strong>.
              </p>
            )}

            {/* iOS non-Safari: must use Safari */}
            {!deferred && iosOther && (
              <p className="text-sm text-[#cbd5e1] mt-1 leading-relaxed">
                Open this page in <strong>Safari</strong>, then tap <Share2 className="h-4 w-4 inline mx-0.5 text-[#00D0E0]" /> <strong>Share</strong> → <strong>Add to Home Screen</strong>. (iPhone only installs apps from Safari.)
              </p>
            )}

            {/* Fallback: no prompt, not iOS (e.g., unsupported desktop browser) */}
            {!deferred && !isIos() && (
              <p className="text-sm text-[#cbd5e1] mt-1 leading-relaxed">
                In Chrome or Edge, use the install icon in the address bar, or the browser menu → <strong>Install app</strong>.
              </p>
            )}
          </div>
        </div>

        {/* Little pointer to the Safari Share button (bottom of screen) */}
        {!deferred && iosSafari && (
          <div className="flex justify-center mt-2">
            <ArrowDown className="h-5 w-5 text-[#00C0C0] animate-bounce" />
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallBanner;
